<template>
  <b-card title="Add User">
    <validation-observer ref="userRules">
      <b-form
        cols="9"
        @submit="onSubmit"
      >
        <b-row>
          <b-col cols="12">
            <b-alert
              v-for="(alertList, index) in alerts"
              :key="`alert_${index}`"
              v-height-fade
              show
              fade
              class="mb-1"
              variant="danger"
            >
              <div class="alert-body">
                <span
                  v-for="(alert, alertIndex) in alertList"
                  :key="`alert_element_${alertIndex}`"
                >{{ alert }}</span>
              </div>
            </b-alert>
          </b-col>
          <b-col cols="12">
            <b-row>
              <!-- first name -->
              <b-col cols="6">
                <b-form-group
                  label="First Name"
                  label-for="first-name"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="First Name"
                    rules="required"
                  >
                    <b-input-group class="input-group-merge">
                      <b-input-group-prepend is-text>
                        <feather-icon icon="UserIcon" />
                      </b-input-group-prepend>
                      <b-form-input
                        id="first-name"
                        v-model="form.first_name"
                        :state="errors.length > 0 ? false:null"
                        placeholder="First Name"
                        required
                        autocomplete="off"
                      />
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <!-- first name -->
              <b-col cols="6">
                <b-form-group
                  label="Last Name"
                  label-for="last-name"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Last Name"
                    rules="required"
                  >
                    <b-input-group class="input-group-merge">
                      <b-input-group-prepend is-text>
                        <feather-icon icon="UserIcon" />
                      </b-input-group-prepend>
                      <b-form-input
                        id="last-name"
                        v-model="form.last_name"
                        :state="errors.length > 0 ? false:null"
                        placeholder="Last Name"
                        required
                        autocomplete="off"
                      />
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>

          <!-- email -->
          <b-col cols="12">
            <b-form-group
              label="Email"
              label-for="email"
            >
              <validation-provider
                #default="{ errors }"
                name="Email"
                rules="required|email"
              >
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="MailIcon" />
                  </b-input-group-prepend>
                  <b-form-input
                    id="email"
                    v-model="form.email"
                    :state="errors.length > 0 ? false:null"
                    type="email"
                    placeholder="Email"
                    required
                    autocomplete="off"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-row>
              <!-- password -->
              <b-col cols="6">
                <b-form-group
                  label="Password"
                  label-for="password"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Password"
                    :rules="passwordRules"
                  >
                    <b-input-group>
                      <b-form-input
                        id="password"
                        v-model="form.password"
                        :state="errors.length > 0 ? false:null"
                        :type="passwordFieldType"
                        placeholder="Your Password"
                        :disabled="passwordDisabled"
                        autocomplete="off"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="passwordToggleIcon"
                          class="cursor-pointer"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col
                cols="6"
                class="pt-2"
              >
                <b-form-group>
                  <b-form-checkbox
                    id="autogenerate_password"
                    v-model="form.autogenerate_password"
                    name="autogenerate_password"
                    value="autogenerate_password"
                    @change="autogeneratePasswordChanged"
                  >
                    Autogenerate Password
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>

          <!-- Account Roles -->
          <b-col
            cols="12"
            class="mt-2"
          >
            <b-card-title>Account Roles</b-card-title>
            <b-form-group
              v-for="(accountRole) in account.accountRoles"
              :key="accountRole.id"
            >
              <b-form-checkbox
                v-if="!accountRole.name.toString().toLowerCase().includes('segment')"
                :key="accountRole.id"
                v-model="form.account_roles"
                :value="accountRole.id"
              >
                {{ accountRole.name }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            class="mt-2"
          >
            <b-card-title>Project Roles</b-card-title>
            <b-row
              v-for="(accountProject) in account.accountProjects"
              :key="accountProject.id"
              class="mt-2"
            >
              <b-col
                cols="12"
                class="ps-2"
              >
                <b-card-sub-title>{{ accountProject.name }}</b-card-sub-title>
                <b-form-group
                  v-for="(projectRole) in accountProject.projectRoles"
                  :key="`${accountProject.id}_${projectRole.id}`"
                  class="mt-2"
                >
                  <b-form-checkbox
                    :key="projectRole.id"
                    v-model="form.project_roles"
                    :value="`${accountProject.id}_${projectRole.id}`"
                  >
                    {{ projectRole.name }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
          <!-- Submit -->
          <b-col cols="12">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              class="mr-1"
              @click.prevent="validationForm"
            >
              Submit
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              :to="{name: 'account-user-list'}"
            >
              Cancel
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-card>
</template>

<script>
import {
    BCard, BRow, BCol, BFormGroup, BFormInput, BFormCheckbox, BForm, BButton, BInputGroup, BInputGroupPrepend, BCardTitle, BCardSubTitle, BInputGroupAppend, BAlert,

} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
    required, email, confirmed, password,
} from '@validations'
import { heightFade } from '@core/directives/animations'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import { addAccountUser, getAccountDetails } from '@/coreApi/coreApiAccount'

export default {
    components: {
        ValidationProvider,
        ValidationObserver,
        BCard,
        BRow,
        BCol,
        BFormGroup,
        BFormInput,
        BFormCheckbox,
        BInputGroup,
        BInputGroupPrepend,
        BForm,
        BButton,
        BCardTitle,
        BCardSubTitle,
        BInputGroupAppend,
        BAlert,
    },
    directives: {
        Ripple,
        heightFade,
    },
    mixins: [togglePasswordVisibility],
    data() {
        return {
            alerts: [],
            account: [],
            form: {
                email: '',
                first_name: '',
                last_name: '',
                password: '',
                autogenerate_password: 'autogenerate_password',
                account_roles: [],
                project_roles: [],
            },
            passwordDisabled: true,
            passwordRules: '',
            required,
            password,
            email,
            confirmed,
        }
    },
    computed: {
        passwordToggleIcon() {
            return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
        },
    },
    created() {
        getAccountDetails()
            .then(res => {
                this.account = res.data
            })
    },
    methods: {
        validationForm() {
            this.alerts = []
            this.$refs.userRules.validate().then(success => {
                if (success) {
                    if (this.form.project_roles.length) {
                        this.onSubmit()
                    } else {
                        this.alerts = {
                            general: {
                                0: 'Select at least one Project Role',
                            },
                        }
                    }
                }
            })
        },
        onSubmit() {
            const formData = { ...this.form }

            const projectRoles = {}

            this.form.project_roles.forEach(projectRole => {
                const item = projectRole.split('_')
                if (typeof projectRoles[item[0]] === 'undefined') {
                    projectRoles[item[0]] = []
                }
                projectRoles[item[0]].push(item[1])
            })

            formData.project_roles = projectRoles

            addAccountUser(formData)
                .then(response => {
                    if (response.data.data.success) {
                        this.$router.push({ name: 'account-user-list' })
                            .then(() => {
                                this.$toast({
                                    component: ToastificationContent,
                                    position: 'top-right',
                                    props: {
                                        title: 'User added successfully',
                                        icon: 'CheckIcon',
                                        variant: 'success',
                                    },
                                })
                            })
                    }
                })
                .catch(error => {
                    if (error.response.data.error.message) {
                        this.alerts = error.response.data.error.message
                    }
                })
        },
        autogeneratePasswordChanged(value) {
            if (value) {
                this.passwordDisabled = true
                this.passwordRules = ''
            } else {
                this.passwordDisabled = false
                this.passwordRules = 'required|password'
            }
        },
    },
}
</script>
